import React, { useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTheme } from '../ThemeContext';

const F = "'Fira Code', monospace";

const Stocks = ({ data, onUpdate, user }) => {
  const { T } = useTheme();
  const [orderAmount, setOrderAmount] = useState('');
  const [orderProfit, setOrderProfit] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const listRef = useRef(null);

  const showFlash = (msg) => { setFlash(msg); setTimeout(() => setFlash(''), 2500); };

  const todayKey = () => new Date().toISOString().slice(0, 10);
  const calcToday = (orders) => orders.filter(o => o.date?.slice(0, 10) === todayKey()).reduce((s, o) => s + (o.profit || 0), 0);
  const calcTotal = (orders) => orders.reduce((s, o) => s + (o.profit || 0), 0);

  const handleAddOrder = async () => {
    const amt = parseFloat(orderAmount); const profit = parseFloat(orderProfit);
    if (!amt || amt <= 0) return showFlash('✗ ENTER VALID ORDER AMOUNT');
    if (isNaN(profit)) return showFlash('✗ ENTER PROFIT / LOSS VALUE');
    setSaving(true);
    const entry = { amount: amt, profit, date: new Date().toISOString() };
    const orders = [...(data.stockOrders || []), entry];
    const tp = calcToday(orders); const total = calcTotal(orders);
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'stocks'), { orders, todayProfit: tp, totalProfit: total }, { merge: true });
      onUpdate({ stockOrders: orders, stocksTodayProfit: tp, stocksTotalProfit: total });
      setOrderAmount(''); setOrderProfit('');
      showFlash('✓ ORDER LOGGED');
    } catch (e) { showFlash('✗ FIREBASE WRITE FAILED'); }
    setSaving(false);
  };

  const handleDeleteOrder = async (idx) => {
    const realIdx = (data.stockOrders || []).length - 1 - idx;
    const orders = [...(data.stockOrders || [])];
    if (!orders[realIdx]) return;
    orders.splice(realIdx, 1);
    const tp = calcToday(orders); const total = calcTotal(orders);
    try {
      await setDoc(doc(db, 'users', user.uid, 'data', 'stocks'), { orders, todayProfit: tp, totalProfit: total }, { merge: true });
      onUpdate({ stockOrders: orders, stocksTodayProfit: tp, stocksTotalProfit: total });
      showFlash('✓ ORDER DELETED');
    } catch (e) { showFlash('✗ DELETE FAILED'); }
    setDeleteConfirm(null);
  };

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  }, [data.stockOrders]);

  const todayProfit = calcToday(data.stockOrders || []);
  const totalProfit = calcTotal(data.stockOrders || []);
  const todayOrders = (data.stockOrders || []).filter(o => o.date?.slice(0, 10) === todayKey());
  const winRate = (() => {
    const orders = data.stockOrders || [];
    if (orders.length === 0) return 0;
    return Math.round((orders.filter(o => o.profit >= 0).length / orders.length) * 100);
  })();

  const dailyPL = (() => {
    const map = {};
    (data.stockOrders || []).forEach(o => { const day = o.date?.slice(0, 10); if (day) map[day] = (map[day] || 0) + o.profit; });
    return Object.entries(map).slice(-15).map(([date, profit]) => ({ date, profit }));
  })();
  const maxDailyPL = Math.max(...dailyPL.map(d => Math.abs(d.profit)), 1);

  const cardS = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '22px 22px', marginBottom: 14, boxShadow: T.isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.06)', transition: 'background .3s,border .3s' };
  const hdrS  = { fontSize: 10, letterSpacing: '0.25em', color: T.text, fontFamily: F, marginBottom: 18, fontWeight: 700, paddingBottom: 10, borderBottom: `1px solid ${T.border}` };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto' }}>
      {flash && <Flash msg={flash} T={T} />}

      {/* Overview Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <SC label="TODAY'S P&L" value={`${todayProfit >= 0 ? '+' : ''}₹${todayProfit.toLocaleString('en-IN')}`} color={todayProfit >= 0 ? T.emerald : T.rose} sub={`${todayOrders.length} orders`} T={T} />
        <SC label="TOTAL PROFIT" value={`${totalProfit >= 0 ? '+' : ''}₹${totalProfit.toLocaleString('en-IN')}`} color={totalProfit >= 0 ? T.amber : T.rose} sub="all time" T={T} />
        <SC label="WIN RATE" value={`${winRate}%`} color={winRate >= 50 ? T.emerald : T.rose} sub={`${(data.stockOrders || []).length} trades`} T={T} />
        <SC label="TOTAL VOLUME" value={`₹${(data.stockOrders || []).reduce((s, o) => s + o.amount, 0).toLocaleString('en-IN')}`} color={T.cyan} sub="invested" T={T} />
      </div>

      {/* Daily P&L Bar Chart */}
      {dailyPL.length > 0 && (
        <div style={cardS}>
          <div style={hdrS}>📊 DAILY P&L CHART</div>
          <div style={{ display: 'flex', alignItems: 'center', height: 120, padding: '0 4px', position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', height: 1, background: T.muted, opacity: T.isDark ? 0.4 : 0.3 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', height: '100%', justifyContent: 'center' }}>
              {dailyPL.map((d, i) => {
                const pct = (Math.abs(d.profit) / maxDailyPL) * 45;
                const isProfit = d.profit >= 0;
                return (
                  <div key={i} style={{ flex: 1, maxWidth: 28, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'center', position: 'relative' }} title={`${d.date}: ₹${d.profit}`}>
                    {isProfit ? (
                      <>
                        <div style={{ height: `${pct}%`, minHeight: 3, width: '70%', background: `linear-gradient(180deg, ${T.emerald}, ${T.emerald}33)`, borderRadius: '3px 3px 0 0', transition: 'height 0.4s' }} />
                        <div style={{ height: `${50 - pct}%` }} />
                      </>
                    ) : (
                      <>
                        <div style={{ height: `${50 - pct}%` }} />
                        <div style={{ height: `${pct}%`, minHeight: 3, width: '70%', background: `linear-gradient(0deg, ${T.rose}, ${T.rose}33)`, borderRadius: '0 0 3px 3px', transition: 'height 0.4s' }} />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 8, color: T.dim }}>
            <span>← {dailyPL[0]?.date?.slice(5)}</span>
            <span style={{ display: 'flex', gap: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 3, background: T.emerald, borderRadius: 1, display:'inline-block' }} /> PROFIT</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 8, height: 3, background: T.rose, borderRadius: 1, display:'inline-block' }} /> LOSS</span>
            </span>
            <span>{dailyPL[dailyPL.length - 1]?.date?.slice(5)} →</span>
          </div>
        </div>
      )}

      {/* Add Order */}
      <div style={cardS}>
        <div style={hdrS}>↗ LOG STOCK ORDER</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
          <IB label="ORDER AMOUNT (₹)" type="number" value={orderAmount} onChange={setOrderAmount} onEnter={handleAddOrder} T={T} />
          <IB label="PROFIT / LOSS (₹)" type="number" value={orderProfit} onChange={setOrderProfit} onEnter={handleAddOrder} placeholder="neg for loss" T={T} />
        </div>
        <BtnX onClick={handleAddOrder} disabled={saving} label={saving ? 'PROCESSING...' : 'EXECUTE ORDER'} color={T.emerald} />
      </div>

      {/* Order History */}
      <div style={cardS}>
        <div style={{ ...hdrS, display: 'flex', justifyContent: 'space-between' }}>
          <span>📋 ORDER HISTORY</span>
          <span style={{ fontSize: 8, color: T.dim, fontWeight: 400 }}>{(data.stockOrders || []).length} TOTAL</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.6fr 0.3fr', gap: 6, padding: '8px 10px', fontSize: 7, letterSpacing: '0.3em', color: T.dim, borderBottom: `1px solid ${T.border}` }}>
          <span>DATE</span><span>AMOUNT</span><span>P&L</span><span>STATUS</span><span></span>
        </div>
        <div ref={listRef} style={{ maxHeight: 260, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${T.scrollC} transparent` }}>
          {(data.stockOrders || []).length === 0 && (
            <div style={{ textAlign: 'center', color: T.dim, fontSize: 10, padding: 30, letterSpacing: '0.2em' }}>NO ORDERS — LOG YOUR FIRST TRADE</div>
          )}
          {[...(data.stockOrders || [])].reverse().map((o, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.6fr 0.3fr', gap: 6, padding: '11px 10px', borderBottom: `1px solid ${T.border}`, fontSize: 10, alignItems: 'center' }}>
              <span style={{ color: T.dim, fontSize: 9 }}>
                {new Date(o.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                <span style={{ marginLeft: 6, fontSize: 8, color: T.muted }}>{new Date(o.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              </span>
              <span style={{ color: T.text }}>₹{o.amount?.toLocaleString('en-IN')}</span>
              <span style={{ color: o.profit >= 0 ? T.emerald : T.rose, fontWeight: 700 }}>{o.profit >= 0 ? '+' : ''}₹{o.profit?.toLocaleString('en-IN')}</span>
              <span style={{ fontSize: 8, letterSpacing: '0.1em', padding: '2px 6px', borderRadius: 3, background: o.profit >= 0 ? `${T.emerald}18` : `${T.rose}18`, color: o.profit >= 0 ? T.emerald : T.rose, textAlign: 'center' }}>
                {o.profit >= 0 ? 'WIN' : 'LOSS'}
              </span>
              <span>
                {deleteConfirm === i ? (
                  <span onClick={() => handleDeleteOrder(i)} style={{ color: T.rose, cursor: 'pointer', fontSize: 8, fontWeight: 900 }}>OK?</span>
                ) : (
                  <span onClick={() => setDeleteConfirm(i)} style={{ color: T.muted, cursor: 'pointer', fontSize: 11, transition: 'color 0.2s' }}
                    onMouseOver={e => e.currentTarget.style.color = T.rose} onMouseOut={e => e.currentTarget.style.color = T.muted}>✕</span>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* Shared */
const Flash = ({ msg, T }) => (
  <div style={{ position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 999, background: msg.startsWith('✓') ? `${T.emerald}18` : `${T.rose}18`, border: `1px solid ${msg.startsWith('✓') ? T.emerald : T.rose}`, color: msg.startsWith('✓') ? T.emerald : T.rose, padding: '10px 28px', borderRadius: 6, fontSize: 11, letterSpacing: '0.2em', fontFamily: F, animation: 'flashIn 0.3s ease' }}>
  {msg}<style>{`@keyframes flashIn { from{opacity:0;transform:translateX(-50%) translateY(-8px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
  </div>
);

const SC = ({ label, value, color, sub, T }) => (
  <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '16px 14px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: T.isDark ? 'none' : '0 2px 10px rgba(0,0,0,0.05)', transition: 'background .3s' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: color, opacity: 0.5 }} />
    <div style={{ fontSize: 7, letterSpacing: '0.3em', color: T.dim, fontFamily: F, marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 900, color, fontFamily: F }}>{value}</div>
    {sub && <div style={{ fontSize: 8, color: T.dim, marginTop: 6, letterSpacing: '0.15em' }}>{sub}</div>}
  </div>
);

const IB = ({ label, type, value, onChange, onEnter, placeholder, T }) => (
  <div>
    <label style={{ fontSize: 8, letterSpacing: '0.35em', color: T.dim, fontFamily: F, marginBottom: 6, display: 'block' }}>&gt; {label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onEnter?.()} placeholder={placeholder || '0.00'}
      style={{ width: '100%', background: T.inputBg, border: 'none', borderBottom: `1.5px solid ${T.inputBdr}`, padding: '10px 8px', color: T.text, fontSize: 14, letterSpacing: '0.1em', outline: 'none', fontFamily: F, caretColor: T.inputFocus }}
      onFocus={e => e.target.style.borderBottomColor = T.inputFocus} onBlur={e => e.target.style.borderBottomColor = T.inputBdr} />
  </div>
);

const BtnX = ({ onClick, disabled, label, color }) => (
  <button onClick={onClick} disabled={disabled}
    style={{ width: '100%', background: 'transparent', border: `1px solid ${color}44`, color, padding: '12px 0', fontSize: 10, letterSpacing: '0.5em', fontWeight: 700, cursor: 'pointer', fontFamily: F, borderRadius: 6, transition: 'all 0.25s' }}
    onMouseOver={e => { e.currentTarget.style.background = color + '15'; e.currentTarget.style.borderColor = color; }}
    onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = color + '44'; }}
  >{label}</button>
);

export default Stocks;
